import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryActionCreatorResult,
  QueryDefinition,
} from '@reduxjs/toolkit/query'
import { createCustomListener } from 'providers/listeners'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Paper, Typography } from '@mui/material'

import { PSDefaultTemplate, PSTemplateRequest } from 'types/template'

import { updateSavedTemplateFailedAction, updateSavedTemplateSuccessAction } from 'store/actions'

import Preloader from 'components/Preloader'

import NewTemplate from 'pages/withdraw-templates/NewTemplate'
import SavedTemplate from 'pages/withdraw-templates/SavedTemplate'

interface Props {
  organizationId?: string
  defaultTemplates?: PSDefaultTemplate[]
  savedTemplates?: PSTemplateRequest[]
  isSavedTemplatesFetching: boolean
  refetchSavedTemplates: () => QueryActionCreatorResult<
    QueryDefinition<
      string,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
      'Organization' | 'Member',
      PSTemplateRequest[],
      'organization'
    >
  >
}

const OrganizationWithdrawTemplates: FC<Props> = ({
  organizationId,
  refetchSavedTemplates,
  savedTemplates,
  defaultTemplates,
  isSavedTemplatesFetching,
}) => {
  const [isSavedUpdating, setIsSavedUpdating] = useState(false)
  const [isAddNewModalOpen, setIsAddNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  useEffect(() => {
    const unsubscribe = createCustomListener({
      actionType: updateSavedTemplateFailedAction.type,
      effect: () => {
        setIsSavedUpdating(false)
      },
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = createCustomListener({
      actionType: updateSavedTemplateSuccessAction.type,
      effect: () => {
        refetchSavedTemplates()
          .unwrap()
          .finally(() => {
            setIsAddNewModalOpen(false)
            setIsEditModalOpen(false)
            setIsSavedUpdating(false)
          })
      },
    })
    return unsubscribe
  }, [])

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Typography variant={'h3'}>
        {t('Pages.Organizations.OrganizationWithdrawTemplates')}
      </Typography>
      {(isSavedUpdating || isSavedTemplatesFetching) && <Preloader />}
      {!!savedTemplates?.length && !!defaultTemplates?.length && (
        <SavedTemplate
          isSavedUpdating={isSavedUpdating}
          savedTemplates={savedTemplates}
          defaultTemplates={defaultTemplates}
          isModalOpen={isEditModalOpen}
          setIsSavedUpdating={setIsSavedUpdating}
          setIsModalOpen={setIsEditModalOpen}
          organizationId={organizationId}
        />
      )}

      {!!defaultTemplates?.length && (
        <NewTemplate
          defaultTemplates={defaultTemplates}
          isSavedUpdating={isSavedUpdating}
          isModalOpen={isAddNewModalOpen}
          setIsSavedUpdating={setIsSavedUpdating}
          setIsModalOpen={setIsAddNewModalOpen}
          organizationId={organizationId}
        />
      )}
    </Paper>
  )
}

export default OrganizationWithdrawTemplates
